import {
  Component,
  ElementRef,
  Input,
  Output,
  EventEmitter,
  inject,
  viewChild,
  signal
} from '@angular/core';
import { ZefReactiveComponent } from '@zerops/zef/core';
import {
  HttpRoutingEntity,
  getCategorizedHttpRoutings,
  triggerDomainsDnsCheck
} from '@zerops/zerops/core/http-routing-base';
import { selectZefProgressMapByType } from '@zerops/zef/progress';
import {
  HttpRoutingItemTranslations,
  HTTP_ROUTING_ITEM_FEATURE_NAME,
  TriggerDnsCheckEvent,
  BadgeClickEvent
} from '@zerops/zui/http-routing-item';
import { HttpRouting, Domain } from '@zerops/models/http-routing';
import { Store, select } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { withLatestFrom, map, startWith } from 'rxjs/operators';
import { ObservableInput } from 'observable-input';
import { HttpRoutingListTranslations } from './http-routing-list.translations';
import { FEATURE_NAME } from './http-routing-list.constant';
import { SatPopover } from '@zerops/zef/popover';
import { BadgeType } from '@zerops/zui/http-routing-item/http-routing-item.model';

@Component({
  selector: 'z-http-routing-list',
  templateUrl: './http-routing-list.container.html',
  styleUrl: './http-routing-list.container.scss'
})
export class HttpRoutingListContainer extends ZefReactiveComponent {

  // # Deps
  #store = inject(Store);
  #httpRoutingEntity = inject(HttpRoutingEntity);

  // # Event Streams
  @Output()
  editOpenClick = new EventEmitter<{
    routing: HttpRouting;
    locationPath?: string;
  }>();

  onTriggerDnsCheck$ = new Subject<TriggerDnsCheckEvent>();

  // # Data
  // -- angular
  @ObservableInput()
  @Input('items')
  items$!: Observable<HttpRouting[]>;

  @ObservableInput()
  @Input('serviceStackId')
  serviceStackId$!: Observable<string>;

  // -- sync
  activeMoreId: string = undefined;
  activeHttpRoutingItemElRef: ElementRef<any>;
  externalVisible = false;
  triggerDnsCheckActionKey = triggerDomainsDnsCheck.type;
  activeDomain = signal<Domain | null>(null);
  activeBadgeType = signal<BadgeType | null>(null);
  activeHttpRoutingId = signal<string | null>(null);

  // # View Queries
  sharedBadgePopoverRef = viewChild<SatPopover>('sharedBadgePopover');

  // -- async
  translations$ = this.translate$<HttpRoutingListTranslations>(FEATURE_NAME);
  httpRoutingItemTranslations$ = this.translate$<HttpRoutingItemTranslations>(HTTP_ROUTING_ITEM_FEATURE_NAME);
  // split into local / external for both items and their locations
  categorizedItems$ = this.items$.pipe(
    withLatestFrom(this.serviceStackId$.pipe(startWith(undefined))),
    map(([ items, serviceStackId ]) => getCategorizedHttpRoutings(items, serviceStackId)),
  );
  loadingsMap$ = this.#store.pipe(
    select(selectZefProgressMapByType([
      this.#httpRoutingEntity.deleteOne.type,
      this.#httpRoutingEntity.restoreOne.type
    ]))
  );
  triggerDnsCheckLoadingsMap$ = this.#store.pipe(
    select(selectZefProgressMapByType([ triggerDomainsDnsCheck.type ]))
  );

  // # State resolver
  state = this.$connect({
    translations: this.translations$,
    httpRoutingItemTranslations: this.httpRoutingItemTranslations$,
    items: this.items$,
    categorizedItems: this.categorizedItems$,
    loadingsMap: this.loadingsMap$,
    triggerDnsCheckLoadingsMap: this.triggerDnsCheckLoadingsMap$
  });

  // # Dispatcher
  constructor() {
    super();

    this.$dispatchActions([
      this.onTriggerDnsCheck$.pipe(
        map(({ httpRoutingId, domainName }) => triggerDomainsDnsCheck({
          httpRoutingId,
          domainNames: domainName ? [ domainName ] : null
        }))
      )
    ]);
  }

  // # Event Handlers
  closePopoverAndResetState(): void {
    this.sharedBadgePopoverRef()?.close();
    this.activeDomain.set(null);
    this.activeBadgeType.set(null);
    this.activeHttpRoutingId.set(null);
  }

  onBadgeClicked(event: BadgeClickEvent): void {
    const popoverRef = this.sharedBadgePopoverRef();

    if (!popoverRef) {
      return;
    }

    const isOpen = popoverRef.isOpen?.();

    // Update signals - this triggers content change via template @if condition
    this.activeDomain.set(event.domain);
    this.activeBadgeType.set(event.type);
    this.activeHttpRoutingId.set(event.httpRoutingId);

    if (isOpen) {
      // If already open, close then reopen at new anchor
      event.anchor.open(false); // Sets anchor without calling open()
      popoverRef.close();
      // Use setTimeout to ensure close completes before reopening
      setTimeout(() => event.anchor.open(), 0);
    } else {
      // If closed, just open at the anchor
      event.anchor.open();
    }
  }

}
